export type InvokeErr =
  | {
      message?: string;
      context?: Response | { body?: string; status?: number };
    }
  | null;

export type InvokeErrorPayload = {
  error?: string;
  message?: string;
  detail?: string;
  cause?: Array<{ description?: string }>;
  errors?: Array<{ message?: string; details?: string[] }>;
};

/** Clasificación de fallos de `functions.invoke` (contrato user-visible Sentry). */
export type InvokeFailureKind = 'ok' | 'business' | 'auth' | 'offline' | 'permission' | 'unexpected';

function extractFirstError(detail: string): string | null {
  try {
    const parsed = JSON.parse(detail) as InvokeErrorPayload;
    return parsed.errors?.[0]?.details?.[0] ?? parsed.cause?.[0]?.description ?? null;
  } catch {
    return null;
  }
}

/** Extrae `error` de negocio del body de respuesta o del context del invoke. */
export function extractInvokeBodyError(data: unknown, error: InvokeErr): string | null {
  const d = data && typeof data === 'object' ? (data as InvokeErrorPayload) : null;
  if (d?.error?.trim()) return d.error.trim();

  const raw = readContextBody(error);
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const j = JSON.parse(raw) as InvokeErrorPayload;
      if (j.error?.trim()) return j.error.trim();
    } catch {
      /* ignore */
    }
  }
  return null;
}

export function messageFromInvoke(data: unknown, error: InvokeErr, fallback: string): string {
  const d = data && typeof data === 'object' ? (data as InvokeErrorPayload) : null;
  if (d?.message?.trim()) {
    let msg = d.message.trim();
    const detail = d.detail?.trim();
    if (detail && !msg.includes(detail.slice(0, 40))) {
      const errDetail = extractFirstError(detail);
      if (errDetail && !msg.includes(errDetail.slice(0, 40))) msg = errDetail;
    }
    return msg;
  }
  if (d?.error?.trim()) return d.error;

  const raw = readContextBody(error);
  if (typeof raw === 'string' && raw.trim()) {
    const errDetail = extractFirstError(raw);
    if (errDetail) return errDetail;
    try {
      const j = JSON.parse(raw) as InvokeErrorPayload;
      if (j.message?.trim()) return j.message;
      if (j.error?.trim()) return j.error;
    } catch {
      /* ignore */
    }
  }
  if (error?.message?.trim()) return error.message;
  return fallback;
}

export function throwIfInvokeError(data: unknown): void {
  if (!data || typeof data !== 'object') return;
  const d = data as InvokeErrorPayload;
  if (d.error) {
    throw new Error(messageFromInvoke(data, null, d.error));
  }
}

function invokeHttpStatus(error: InvokeErr): number | null {
  const ctx = error?.context;
  if (!ctx) return null;
  if (typeof (ctx as Response).status === 'number') return (ctx as Response).status;
  return null;
}

function readContextBody(error: InvokeErr): string | null {
  const ctx = error?.context;
  if (!ctx) return null;
  if ('body' in ctx && typeof (ctx as { body?: string }).body === 'string') {
    return (ctx as { body: string }).body;
  }
  return null;
}

function isOfflineInvokeMessage(text: string): boolean {
  return (
    text.includes('networkerror') ||
    text.includes('a network error occurred') ||
    text.includes('failed to fetch') ||
    text.includes('failed to send a request') ||
    text.includes('err_network') ||
    text.includes('load failed') ||
    text.includes('network request failed') ||
    text.includes('timeout') ||
    text.includes('timed out') ||
    text.includes('connection refused') ||
    text.includes('connection reset')
  );
}

function isAuthInvokeMessage(text: string, status: number | null): boolean {
  if (status === 401) return true;
  return (
    text.includes('jwt expired') ||
    text.includes('invalid jwt') ||
    text.includes('pgrst303') ||
    text.includes('sesión requerida') ||
    text.includes('sesion requerida') ||
    text.includes('session required') ||
    text.includes('not authenticated') ||
    text.includes('iniciá sesión') ||
    text.includes('inicia sesion') ||
    text.includes('auth session missing')
  );
}

/** Auth/sesión expirada — esperado; no va a Sentry como crash (VUELTO-NEGOCIO-2B). */
export function isAuthErrorMessage(raw: string | null | undefined): boolean {
  if (!raw?.trim()) return false;
  return isAuthInvokeMessage(raw.trim().toLowerCase(), null);
}

function isPermissionInvokeMessage(text: string, status: number | null): boolean {
  if (status === 403) return true;
  return (
    text.includes('module_permission_denied') ||
    text.includes('branch_access_denied') ||
    text.includes('permission denied') ||
    text.includes('forbidden') ||
    text.includes('sin permiso') ||
    text.includes('no tenés permiso') ||
    text.includes('no tenes permiso')
  );
}

/** Códigos / fragmentos de producto conocidos (sin body.error parseable). */
function isKnownBusinessInvokeMessage(text: string): boolean {
  return (
    text.includes('already_') ||
    text.includes('not_found') ||
    text.includes('invalid_') ||
    text.includes('validation') ||
    text.includes('duplicate') ||
    text.includes('limit_exceeded') ||
    text.includes('rate_limit') ||
    text.includes('handoff_invalid') ||
    text.includes('handoff_expired') ||
    text.includes('código expirado') ||
    text.includes('codigo expirado')
  );
}

/**
 * Clasifica el resultado de un Edge `functions.invoke`.
 * - `business` / `auth` / `offline` / `permission` → toast concreto, sin `user_visible`
 * - `unexpected` → `presentUnexpectedFailure` (toast genérico + Sentry)
 */
export function classifyInvokeFailure(data: unknown, error: InvokeErr): InvokeFailureKind {
  const bodyError = extractInvokeBodyError(data, error);
  const resolved = messageFromInvoke(data, error, '');
  const text = [bodyError ?? '', error?.message ?? '', resolved].join(' ').toLowerCase();
  const status = invokeHttpStatus(error);

  if (!error && !bodyError) return 'ok';

  if (isAuthInvokeMessage(text, status)) return 'auth';
  if (isOfflineInvokeMessage(text)) return 'offline';
  // 403 sin body de negocio = permiso Vuelto. Con body.error (p. ej. padrón ARCA) es business.
  if (isPermissionInvokeMessage(text, bodyError ? null : status)) return 'permission';
  if (bodyError) return 'business';
  if (isKnownBusinessInvokeMessage(text)) return 'business';

  // 4xx sin body.error parseable: tratar como negocio si el mensaje no es el genérico de Functions.
  if (status != null && status >= 400 && status < 500) {
    const generic = text.includes('non-2xx') || text.includes('edge function');
    if (!generic && text.trim()) return 'business';
  }

  return 'unexpected';
}
